import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import Media from 'react-media';
import { FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
// eslint-disable-next-line import/extensions
import { connect } from 'redux-zero/react';
import { Formik } from 'formik';
import cn from 'classnames';
import { isValidNumber } from 'libphonenumber-js';
import { vatCodeValidator, vatCodeIsEnterprise } from '../../shared/validator.js';

import Layout from '../../components/layout/layout.component.js';
import CheckoutModel from './checkout.model.js';
import { actions } from '../../shared/store.js';
import { withLocaleContext } from '../../locales/locale-context.js';
import CheckoutService from './checkout.service.js';
import ExperiencesStep from './experiences.step.js';
import PeopleAndExtrasStep from './people-and-extras.step.js';
import AvailabilityStep from './availability.step.js';
import CustomerDetailsStep from './customer-details.step.js';
import PaymentDetailsStep from './payment-details.step.js';
import InfoMessage from '../../components/info-message/info-message.component.js';
import PhibiGif from '../../assets/images/checkout/phibi.gif';
import Navbar from '../../layout/navbar.component.js';
import CheckoutSummaryPanel from './checkout-summary-panel.js';
import CheckoutSummaryBar from './checkout-summary-bar.js';

import MainService from '../../shared/main.service.js';

import './checkout.page.scss';

const NEXT_BUTTON_MESSAGES_PER_STEP = {
	0: <FormattedMessage id="checkout.continue-to-people-and-extras" />,
	1: <FormattedMessage id="checkout.continue-to-day-and-hour" />,
	2: <FormattedMessage id="checkout.continue-to-customer-details" />,
	3: <FormattedMessage id="checkout.continue-to-payment" />,
};

const CheckoutSuccess = ({ successOrderReference }) => (
	<div className="checkout-success row middle-xs">
		<div className="col-xs-12">
			<div className="container container-fluid">
				<img src={PhibiGif} className="phibi" alt="Phibi" />

				<h1>
					<FormattedMessage id="checkout.success.thank-you" />
				</h1>
				<p>
					<FormattedMessage id="checkout.success.order-reference" />
					{' '}
					<Link
						to={`/check-my-booking/${successOrderReference}`}
						className="reference"
					>
						{`#${successOrderReference}`}
					</Link>
					{'.'}
					<br />
					<FormattedMessage id="checkout.success.check-email" />
				</p>

				<Link to="/" className="back-button">
					<Media query="(min-width: 375px)">
						{matches => (matches ? (
							<FormattedMessage id="button.back-to-homepage" />
						) : (
							<FormattedMessage id="navigation.homepage" />
						))
						}
					</Media>
				</Link>
			</div>
		</div>
	</div>
);
const Step = ({ children }) => <Fragment>{children}</Fragment>;

class CheckoutPageInner extends Component {
	constructor(props) {
		super(props);
		this.formikRef = React.createRef();
		this.state = {
			page: 0,
			checkoutSuccess: false,
			successOrderReference: null,
			validPages: [0],
			isCurrentPageValid: false,
			initialState: null,
		};
	}

	componentDidMount() {
		const { checkoutFilters } = this.props;
		if (checkoutFilters) {
			this.setState({ initialState: checkoutFilters, page: 1, validPages: [0, 1] });
		} else {
			const initialState = new CheckoutModel({
				prices: [],
				extras: [],
				experience: null,
				schedule: null,
				selectedMethod: 'email',
			});
			this.setState({ initialState });
		}
	}

	next = () => {
		const { children } = this.props;
		const { validPages, page } = this.state;

		const validPagesCopy = [...validPages];

		if (!validPagesCopy.includes(page + 1)) {
			validPagesCopy.push(page + 1);
		}

		this.setState(state => ({
			page: Math.min(state.page + 1, children.length - 1),
		}));
		this.setState({ validPages: validPagesCopy });
	};

	goTo = pos => this.setState({
		page: pos,
	});

	previous = () => this.setState(state => ({
		page: Math.max(state.page - 1, 0),
	}));

	validate = (values) => {
		const { children } = this.props;
		const { page, validPages } = this.state;

		const activePage = React.Children.toArray(children)[page];

		const errors = activePage.props.validate
			? activePage.props.validate(values)
			: {};

		this.setState({
			isCurrentPageValid: Object.keys(errors).length === 0,
		});

		if (Object.keys(errors).length > 0) {
			const validPagesCopy = [...validPages];
			validPagesCopy.splice(
				validPagesCopy.indexOf(page) + 1,
				validPagesCopy.length - validPagesCopy.indexOf(page),
			);

			this.setState({
				validPages: validPagesCopy,
			});
		}

		return errors;
	};

	componentDidUpdate(prevProps, prevState) {
		const { checkoutSuccess, page } = this.state;

		if (prevState.page !== page) {
			const tmp = this.validate(this.formikRef.current.state.values);

			this.setState({
				isCurrentPageValid: Object.keys(tmp).length === 0,
			});

			if (!checkoutSuccess) {
				this.slideToCheckoutNavbar();
			}
		}
	}

	handleSubmit = (values, bag) => {
		const {
			stripeData, locale, intl, infoMessage, handleInfoMessage, setCheckoutFilters,
		} = this.props;

		if (infoMessage) {
			handleInfoMessage({});
		}

		const payment = { intentId: stripeData.intentId };

		const customer = {
			firstName: values.firstName,
			lastName: values.lastName,
			email: values.email,
			phoneNumber: values.phone,
		};

		if (values.addBillingAddress) {
			customer.address = {
				address1: values.address,
				city: values.city,
				countryCode: values.country.value,
				postalCode: values.postalCode,
				vatNumber: values.vatNumber,
			};
		}

		const date = moment(values.schedule.day);
		date.set({
			hour: values.schedule.time.split(':')[0],
			minute: values.schedule.time.split(':')[1],
		});

		const experiences = [
			{
				id: values.id,
				date: date.toISOString(),
				session: values.schedule.session,
				tickets: values.prices.map(ticket => ({
					id: ticket.id,
					quantity: ticket.quantity,
					type: ticket.id,
				})),
				extras: values.extras.map(extra => (
					{ extra: extra.id, quantity: extra.quantity }
				)),
			},
		];

		const language = locale || 'pt';

		const body = {
			experiences,
			customer,
			payment,
			language,
		};

		if (values.selectedMethod === 'email+sms') {
			body.sms = true;
		}

		CheckoutService.checkout({ ...body })
			.then((response) => {
				bag.setTouched({});
				bag.setSubmitting(false);
				this.setState({
					checkoutSuccess: true,
					successOrderReference: response.reference,
				});
				this.slideToTop();
				setCheckoutFilters(null);
			})
			.catch(() => {
				handleInfoMessage({
					message: intl.formatMessage({ id: 'checkout.error.submitted-error' }),
					type: 'error',
				});
				bag.setTouched({});
				bag.setSubmitting(false);
			});
	};

	slideToCheckoutNavbar() {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	slideToTop() {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	render() {
		const {
			children,
			infoMessage,
			handleInfoMessage,
		} = this.props;

		const {
			page,
			checkoutSuccess,
			validPages,
			isCurrentPageValid,
			initialState,
			successOrderReference,
		} = this.state;

		if (!initialState) {
			return null;
		}

		if (checkoutSuccess) {
			return (
				<Fragment>
					<Navbar />
					<div id="checkout-page">
						<CheckoutSuccess successOrderReference={successOrderReference} />
					</div>
				</Fragment>
			);
		}

		const activePage = React.Children.toArray(children)[page];
		const isLastPage = page === React.Children.count(children) - 1;

		const labels = [
			'checkout.navbar.select-the-experience',
			'checkout.navbar.people-and-extras',
			'checkout.navbar.availability',
			'checkout.navbar.customer-details',
			'checkout.navbar.payment',
		];

		return (
			<Fragment>
				<Navbar />

				<div id="checkout-page">
					<Media query="(min-width: 994px)">
						{matches => (matches ? (
							<div className="checkout-wizard-navbar row center-xs">
								<ul>
									{Array(5)
										.fill(0)
										.map((value, index) => (
											<li
												onClick={() => this.goTo(index)}
												className={cn(
													{ active: page === index },
													{ disabled: !validPages.includes(index) },
												)}
											>
												<FormattedMessage id={labels[index]} />
											</li>
										))}
								</ul>
							</div>
						) : (
							<div className="checkout-wizard-navbar row">
								<div className="col-xs-12 center-xs">
									<span className="select">
										<FormattedMessage id="checkout.navbar.select-the-experience" />
									</span>
								</div>
								<div className="col-xs-12 center-xs">
									<div className="points">
										{Array(5)
											.fill(0)
											.map((value, index) => (
												<div
													key={`${value}-${index}`}
													onClick={() => this.goTo(index)}
													className={cn(
														'point',
														{ active: page === index },
														{ passed: page > index },
														{ disabled: !validPages.includes(index) },
													)}
												/>
											))}
									</div>
								</div>
							</div>
						))
						}
					</Media>

					<InfoMessage
						infoMessage={infoMessage}
						handleClose={() => handleInfoMessage({})}
					/>

					<div className="container container-fluid">
						<div className="form-container">
							<Formik
								ref={this.formikRef}
								initialValues={initialState}
								enableReinitialize={false}
								validate={this.validate}
								onSubmit={this.handleSubmit}
								validateOnChange
								isInitialValid
								render={({ isSubmitting }) => (
									<form>
										<div className="row">
											<div className={cn('col-xs-12', { 'col-md-6': page !== 0 })}>
												<div className="form-wrapper">
													{activePage}
													<div
														className={cn('action-buttons-container', {
															'as-bar': page === 0,
														})}
													>
														<div className="action-buttons-container">
															{page > 0 && (
																<button
																	type="button"
																	onClick={this.previous}
																	className="back-button"
																>
																	<FormattedMessage id="button.back" />
																</button>
															)}
															{(
																(!isLastPage && page !== 0)
																|| (page === 0 && isCurrentPageValid && validPages.length > 1)
															)
															&& (
																<button
																	type="button"
																	onClick={this.next}
																	disabled={isSubmitting || !isCurrentPageValid}
																	className="next-button"
																>
																	{NEXT_BUTTON_MESSAGES_PER_STEP[page]}
																</button>
															)}

															{isLastPage && <div id="complete_order_btn" />}
														</div>
													</div>
												</div>
											</div>
											<Media query="(min-width: 994px)">
												{matches => (matches ? (
													<Fragment>
														{page !== 0
															&& (
																<div className="col-md-4 col-md-offset-1">
																	<CheckoutSummaryPanel />
																</div>
															)
														}
													</Fragment>
												) : (
													<CheckoutSummaryBar />
												))
												}
											</Media>
										</div>
									</form>
								)}
							/>
						</div>
					</div>
				</div>
			</Fragment>
		);
	}
}

// eslint-disable-next-line react/no-multi-comp
class CheckoutPage extends Component {
	static prefetchData = async language => MainService.getContactsData(language);

	constructor(props) {
		super(props);
		this.checkoutPageInnerRef = React.createRef();
		this.state = {
			stripe: null,
			stripeData: null,
			infoMessage: null,
		};
	}

	componentDidMount() {
		const {
			setLoading, setCheckoutExperience, checkoutExperience, checkoutFilters,
		} = this.props;

		if (checkoutFilters) {
			setCheckoutExperience(checkoutFilters);
			setLoading(false);
		} else if (!checkoutExperience) {
			this.fetchData();
		} else {
			setLoading(false);
		}

		// componentDidMount only runs in a browser environment.
		// In addition to loading asynchronously, this code is safe to server-side render.

		// You can inject a script tag manually like this,
		// or you can use the 'async' attribute on the Stripe.js v3 <script> tag.
		const stripeJs = document.createElement('script');
		stripeJs.src = 'https://js.stripe.com/v3/';
		stripeJs.async = true;
		stripeJs.onload = () => {
			this.setState({
				stripe: window.Stripe(process.env.REACT_APP_STRIPE_KEY),
			});
		};

		document.body && document.body.appendChild(stripeJs);
	}

	componentDidUpdate(prevProps) {
		const { localeContext } = this.props;

		if (prevProps.localeContext.locale.value !== localeContext.locale.value) {
			this.fetchData();
		}
	}

	componentWillUnmount() {
		const { setCheckoutFilters } = this.props;
		setCheckoutFilters(null);
	}

	handleInfoMessage = ({ message, type }) => {
		if (!message && !type) {
			this.setState({ infoMessage: null });
		} else if (message && !type) {
			this.setState({ infoMessage: { message, type: null } });
		} else {
			this.setState({ infoMessage: { message: message || null, type: type || null } });
		}
	};

	fetchData() {
		const {
			setLoading,
			setLoaded,
			setContentData,
			localeContext: { locale },
		} = this.props;

		setLoading(true);
		setLoaded(false);

		CheckoutPage.prefetchData(locale.value)
			.then((contentData) => {
				setLoading(false);
				setContentData(contentData);
				setLoaded(true);
			})
			.catch(() => {
				setLoading(false);
				setLoaded(false);

				this.setState({
					hasError: true,
				});
			});
	}

	handleCard = (stripeData) => {
		this.setState({ stripeData });
	};

	render() {
		const {
			intl,
			checkoutExperience,
			localeContext,
			isLoading,
			contentData,
			checkoutFilters,
			setCheckoutFilters,
		} = this.props;

		const {
			stripe, stripeData, hasError, infoMessage,
		} = this.state;

		if (isLoading || hasError || !contentData) {
			return null;
		}

		const { privacyAndPolicyPDF } = contentData;

		return (
			<Layout>
				<CheckoutPageInner
					stripeData={stripeData}
					stripe={stripe}
					locale={localeContext.locale.value}
					intl={intl}
					infoMessage={infoMessage}
					handleInfoMessage={this.handleInfoMessage}
					checkoutFilters={checkoutFilters}
					setCheckoutFilters={setCheckoutFilters}
					ref={this.checkoutPageInnerRef}
				>
					<Step
						validate={(values) => {
							const errors = {};
							if (!values.id) {
								errors.experience = intl.formatMessage({
									id: 'checkout.validation.experience',
								});
							}
							return errors;
						}}
					>
						<ExperiencesStep
							handleNextPage={() => this.checkoutPageInnerRef.current.next()}
						/>
					</Step>

					<Step
						validate={(values) => {
							const errors = {};

							const initialValue = 0;
							const sum = values.prices.reduce(
								(accumulator, currentValue) => accumulator + currentValue.quantity,
								initialValue,
							);

							if (sum < 0) {
								errors.prices = intl.formatMessage({
									id: 'checkout.validation.adults',
								});
							}

							return errors;
						}}
					>
						<PeopleAndExtrasStep />
					</Step>

					<Step
						validate={(values) => {
							const errors = {};
							if (!values.schedule.day) {
								errors.date = intl.formatMessage({
									id: 'checkout.validation.date',
								});
							}
							if (!values.schedule.time) {
								errors.time = intl.formatMessage({
									id: 'checkout.validation.date',
								});
							}
							return errors;
						}}
					>
						<AvailabilityStep
							schedule={checkoutExperience ? checkoutExperience.schedule : null}
							handleInfoMessage={this.handleInfoMessage}
						/>
					</Step>

					<Step
						validate={(values) => {
							const errors = {};
							if (!values.firstName) {
								errors.firstName = intl.formatMessage({
									id: 'checkout.validation.required.name',
								});
							}
							if (!values.lastName) {
								errors.lastName = intl.formatMessage({
									id: 'checkout.validation.required.surname',
								});
							}
							if (!values.email) {
								errors.email = intl.formatMessage({
									id: 'checkout.validation.required.email',
								});
							} else if (
								!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
							) {
								errors.email = intl.formatMessage({
									id: 'checkout.validation.invalid.email',
								});
							}

							 if (values.phone && !isValidNumber(values.phone)) {
								errors.phone = intl.formatMessage({
									id: 'checkout.validation.invalid.phone',
								});
							}

							if (values.addBillingAddress) {
								if (!values.country) {
									errors.country = intl.formatMessage({
										id: 'checkout.validation.required.country',
									});
								}
								if (!values.vatNumber) {
									errors.vatNumber = intl.formatMessage({
										id: 'checkout.validation.required.vatNumber',
									});
								} else if (
									!vatCodeValidator((values.country ? values.country.value : null), values.vatNumber)
								) {
									errors.vatNumber = intl.formatMessage({
										id: 'checkout.validation.invalid.vatNumber',
									});
								} else if (vatCodeIsEnterprise((values.country ? values.country.value : null), values.vatNumber)) {
									if (!values.address) {
										errors.address = intl.formatMessage({
											id: 'checkout.validation.required.address',
										});
									}
									if (!values.postalCode) {
										errors.postalCode = intl.formatMessage({
											id: 'checkout.validation.required.postalCode',
										});
									}
									if (!values.city) {
										errors.city = intl.formatMessage({
											id: 'checkout.validation.required.city',
										});
									}
								}
							}

							if (!values.acceptPrivacyPolicy) {
								errors.acceptPrivacyPolicy = intl.formatMessage({
									id: 'checkout.validation.acceptPrivacyPolicy',
								});
							}
							return errors;
						}}
					>
						<CustomerDetailsStep privacyAndPolicyPDF={privacyAndPolicyPDF} />
					</Step>

					<Step>
						<PaymentDetailsStep stripe={stripe} handleCard={this.handleCard} />
					</Step>
				</CheckoutPageInner>
			</Layout>
		);
	}
}

const mapToProps = ({
	contentData, checkoutExperience, isLoading, checkoutFilters, checkoutExperiences,
}) => ({
	checkoutExperience,
	contentData,
	isLoading,
	checkoutFilters,
	checkoutExperiences,
});

export default withRouter(
	injectIntl(
		connect(
			mapToProps,
			actions,
		)(withLocaleContext(CheckoutPage)),
	),
);

export { CheckoutPage };
